import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, MapPin, Calendar, Clock, Star, CheckCircle2,
  ArrowRight, Hammer, Quote, X, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';

import deckpro1 from '@/assets/deckpro (1).jpeg';
import deckpro2 from '@/assets/deckpro (2).jpeg';
import deckpro3 from '@/assets/deckpro (3).jpeg';
import deckpro4 from '@/assets/deckpro (4).jpeg';
import deckpro5 from '@/assets/deckpro (5).jpeg';
import deckpro6 from '@/assets/deckpro (6).jpeg';
import deckpro7 from '@/assets/deckpro (7).jpeg';
import deckpro8 from '@/assets/deckpro (8).jpeg';
import deckpro9 from '@/assets/deckpro (9).jpeg';
import deckpro10 from '@/assets/deckpro (10).jpeg';
import deckpro11 from '@/assets/deckpro (11).jpeg';
import deckpro12 from '@/assets/deckpro (12).jpeg';
import deckpro13 from '@/assets/deckpro (13).jpeg';
import deckpro14 from '@/assets/deckpro (14).jpeg';
import deckpro15 from '@/assets/deckpro (15).jpeg';
import deckpro16 from '@/assets/deckpro (16).jpeg';
import deckpro17 from '@/assets/deckpro (17).jpeg';
import deckpro18 from '@/assets/deckpro (18).jpeg';
import deckpro19 from '@/assets/deckpro (19).jpeg';
import deckpro20 from '@/assets/deckpro (20).jpeg';
import deckpro21 from '@/assets/deckpro (21).jpeg';
import deckpro22 from '@/assets/deckpro (22).jpeg';
import deckpro23 from '@/assets/deckpro (23).jpeg';
import deckpro24 from '@/assets/deckpro (24).jpeg';
import deckpro25 from '@/assets/deckpro (25).jpeg';
import deckpro26 from '@/assets/deckpro (26).jpeg';
import deckpro27 from '@/assets/deckpro (27).jpeg';
import deckpro28 from '@/assets/deckpro (28).jpeg';
import deckpro29 from '@/assets/deckpro (29).jpeg';
import deckpro30 from '@/assets/deckpro (30).jpeg';
import deckpro31 from '@/assets/deckpro (31).jpeg';
import deckpro32 from '@/assets/deckpro (32).jpeg';
import deckpro33 from '@/assets/deckpro (33).jpeg';
import deckpro34 from '@/assets/deckpro (34).jpeg';
import deckpro35 from '@/assets/deckpro (35).jpeg';
import deckpro36 from '@/assets/deckpro (36).jpeg';
import deckpro37 from '@/assets/deckpro (37).jpeg';
import deckpro38 from '@/assets/deckpro (38).jpeg';
import deckpro39 from '@/assets/deckpro (39).jpeg';
import deckpro40 from '@/assets/deckpro (40).jpeg';
import deckpro41 from '@/assets/deckpro (41).jpeg';
import deckpro42 from '@/assets/deckpro (42).jpeg';

/* ─── Data ─────────────────────────────────────────── */
interface Project {
  id: number;
  slug: string;
  name: string;
  title: string;
  category: string;
  location: string;
  county: string;
  date: string;
  duration: string;
  size: string;
  rating: number;
  description: string;
  materials: string[];
  features: string[];
  images: string[];
  testimonial: {
    text: string;
    author: string;
    role: string;
  };
}

const projects: Project[] = [
  {
    id: 1,
    slug: 'pressure-treated-wood-deck',
    name: 'Project 01',
    title: 'Pressure-Treated Wood Deck with Lattice',
    category: 'Wood',
    location: 'Morris County',
    county: 'Morris County, NJ',
    date: 'Spring 2024',
    duration: '5 weeks',
    size: '1,800 sq ft',
    rating: 5,
    description: 'A 1,800 sq ft multi-level pressure-treated wood deck with stainless cable railings, decorative white lattice skirting, and multiple staircases. Designed for durability and classic New Jersey backyard curb appeal.',
    materials: ['Pressure-Treated Lumber', 'Stainless Steel Cable Rails', 'White Vinyl Lattice', 'Exterior Deck Screws', 'Weather Sealant'],
    features: ['Multi-Level Layout', 'Cable Railings', 'Lattice Skirting', 'Professional Finishing', 'Weather Resistant'],
    images: [deckpro1, deckpro2, deckpro3, deckpro4, deckpro5, deckpro6, deckpro7, deckpro8, deckpro42],
    testimonial: {
      text: 'The DeckPros NJ team delivered exceptional craftsmanship. Our new deck is not only beautiful but built to last. Highly recommend!',
      author: 'Morris County Homeowner',
      role: 'Verified Client',
    },
  },
  {
    id: 2,
    slug: 'multi-level-composite-deck',
    name: 'Project 02',
    title: 'Multi-Level Composite Deck with Under-Deck Living',
    category: 'Multi-Level Composite',
    location: 'Sussex County',
    county: 'Sussex County, NJ',
    date: 'Summer 2023',
    duration: '7 weeks',
    size: '2,200 sq ft',
    rating: 5,
    description: 'A stunning 2,200 sq ft white composite deck with multiple levels, lattice-pattern railings, and a fully waterproofed under-deck living space — perfect for NJ\'s lake country climate.',
    materials: ['Premium White Composite', 'Aluminum Railings', 'Decorative Lattice', 'Under-Deck Membrane', 'Stainless Hardware'],
    features: ['Multi-Level', 'White Composite', 'Under-Deck Living', 'Lattice Railings', 'Waterproofed'],
    images: [deckpro9, deckpro10, deckpro11, deckpro12, deckpro13, deckpro14, deckpro15, deckpro16, deckpro17, deckpro18, deckpro19, deckpro20, deckpro21, deckpro22, deckpro23],
    testimonial: {
      text: 'The DeckPros team created exactly what we envisioned. The quality of materials and workmanship is outstanding. Our new deck is the perfect gathering place!',
      author: 'Sussex County Homeowner',
      role: 'Verified Client',
    },
  },
  {
    id: 3,
    slug: 'premium-architectural-composite',
    name: 'Project 03',
    title: 'Premium Composite Deck with Architectural Details',
    category: 'Architectural Composite',
    location: 'Somerset County',
    county: 'Somerset County, NJ',
    date: 'Spring 2024',
    duration: '6 weeks',
    size: '2,000+ sq ft',
    rating: 5,
    description: 'A 2,000+ sq ft premium gray composite deck with architectural detailing, seamless home integration, and a modern minimalist railing system. Engineered for low maintenance and long-term durability.',
    materials: ['Premium Gray Composite (Trex/TimberTech)', 'Aluminum Sub-Frame', 'Stainless Hardware', 'High-Grade Fasteners', 'Professional Sealant'],
    features: ['Modern Design', 'Premium Composite', 'Seamless Integration', 'Low Maintenance', 'Architectural Detailing'],
    images: [deckpro24, deckpro25, deckpro26, deckpro27, deckpro28, deckpro29, deckpro30, deckpro31, deckpro32, deckpro33, deckpro34, deckpro35, deckpro36, deckpro37, deckpro38, deckpro39, deckpro40, deckpro41],
    testimonial: {
      text: 'DeckPros NJ delivered outstanding craftsmanship and professionalism throughout the project. The deck looks absolutely stunning and adds tremendous value to our home.',
      author: 'Somerset County Homeowner',
      role: 'Verified Client',
    },
  },
];

/* ─── Hero ──────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Full Case Studies</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          Project <span className="text-lime">Showcase</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          Three signature deck projects built by DeckPros NJ — with materials, timelines, photos, and homeowner testimonials from across New Jersey.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Project navigator tabs ───────────────────────── */
const ProjectTabs = ({
  activeIndex,
  setActiveIndex,
}: {
  activeIndex: number;
  setActiveIndex: (i: number) => void;
}) => (
  <section className="bg-cream-dark border-b-2 border-navy/10 sticky top-14 z-30">
    <div className="container mx-auto px-4">
      <div className="flex flex-wrap justify-center gap-2 py-4">
        {projects.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setActiveIndex(i)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] border-2 transition-all ${
              activeIndex === i
                ? 'bg-lime border-navy text-navy shadow-hard'
                : 'bg-white border-navy text-navy hover:bg-lime hover:border-navy'
            }`}
          >
            <span className="text-[10px] opacity-70">{p.name}</span>
            <span>{p.category}</span>
          </button>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Project detail block ────────────────────────── */
const ProjectDetails = ({ project }: { project: Project }) => {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const prev = () => setIndex((i) => (i - 1 + project.images.length) % project.images.length);
  const next = () => setIndex((i) => (i + 1) % project.images.length);

  return (
    <section ref={ref} className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <article
          key={project.id}
          itemScope
          itemType="https://schema.org/CreativeWork"
          className="max-w-7xl mx-auto"
        >
          {/* Meta bar above image */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            className="flex flex-wrap items-center justify-between gap-3 mb-5"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-block bg-lime border-2 border-navy font-display font-bold text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 text-navy">
                {project.name} · {project.category}
              </span>
              <h2
                itemProp="name"
                className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy uppercase leading-tight"
                data-speakable
              >
                {project.title}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: project.rating }).map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber text-amber" />
              ))}
              <span className="ml-2 text-xs font-display font-bold uppercase tracking-wider text-stone">
                Verified Project
              </span>
            </div>
          </motion.div>

          {/* Image slider */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="relative border-4 border-navy shadow-hard overflow-hidden mb-4"
          >
            <div className="relative aspect-[16/10] bg-navy">
              <AnimatePresence mode="wait">
                <motion.img
                  key={project.images[index]}
                  src={project.images[index]}
                  alt={`${project.title} — photo ${index + 1} of ${project.images.length}, built in ${project.county}`}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setLightboxOpen(true)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  itemProp="image"
                />
              </AnimatePresence>

              {/* Zoom hint */}
              <button
                onClick={() => setLightboxOpen(true)}
                className="absolute top-3 right-3 bg-navy border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center gap-1.5 px-3 py-1.5 font-display font-bold text-[10px] uppercase tracking-wider transition-colors"
              >
                <Camera className="w-3 h-3" />
                Full Size
              </button>

              {/* Prev/Next */}
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-navy/85 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-navy/85 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Counter */}
              <div className="absolute bottom-3 right-3 bg-navy border-2 border-lime text-lime px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider">
                {index + 1} / {project.images.length}
              </div>
            </div>
          </motion.div>

          {/* Thumbnails */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mx-2 px-2">
            {project.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`View photo ${i + 1}`}
                className={`flex-shrink-0 w-20 h-20 border-2 transition-all ${
                  i === index
                    ? 'border-lime shadow-hard'
                    : 'border-navy/10 hover:border-lime opacity-70 hover:opacity-100'
                }`}
              >
                <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Info grid */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Description + meta */}
            <div className="lg:col-span-2 bg-white border-2 border-navy/10 p-6 md:p-7">
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-lime flex-shrink-0" />
                  <span className="font-display font-bold uppercase tracking-wider text-[11px] text-stone">Location:</span>
                  <span itemProp="contentLocation" className="text-navy font-body font-medium">{project.county}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-lime flex-shrink-0" />
                  <span className="font-display font-bold uppercase tracking-wider text-[11px] text-stone">Completed:</span>
                  <span itemProp="dateCreated" className="text-navy font-body font-medium">{project.date}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-lime flex-shrink-0" />
                  <span className="font-display font-bold uppercase tracking-wider text-[11px] text-stone">Duration:</span>
                  <span className="text-navy font-body font-medium">{project.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Hammer className="w-4 h-4 text-lime flex-shrink-0" />
                  <span className="font-display font-bold uppercase tracking-wider text-[11px] text-stone">Size:</span>
                  <span className="text-navy font-body font-medium">{project.size}</span>
                </div>
              </div>

              <p itemProp="description" className="text-stone text-base leading-relaxed font-body mb-5">
                {project.description}
              </p>

              <Link to="/contact">
                <Button variant="cta" size="lg">
                  Get Yours Quoted
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Materials + Features */}
            <div className="space-y-4">
              <div className="bg-navy text-white border-2 border-navy shadow-hard p-5">
                <p className="section-eyebrow" style={{ color: 'hsl(var(--lime))' }}>Materials</p>
                <h3 className="font-display text-lg font-bold uppercase tracking-wide mb-3 mt-1">
                  What We Used
                </h3>
                <ul className="space-y-1.5">
                  {project.materials.map((m) => (
                    <li key={m} className="flex items-start gap-2 text-sm text-white/85 font-body">
                      <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border-2 border-navy/15 p-5">
                <p className="section-eyebrow">Key Features</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {project.features.map((f) => (
                    <span
                      key={f}
                      className="inline-block bg-lime/20 border border-lime text-navy font-display font-bold text-[10px] uppercase tracking-wider px-2.5 py-1"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            itemScope
            itemProp="review"
            itemType="https://schema.org/Review"
            className="relative bg-navy text-white border-4 border-lime p-8 md:p-10 max-w-4xl mx-auto"
          >
            <Quote className="absolute top-3 left-4 w-14 h-14 text-lime/20" />
            <div className="relative">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: project.rating }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber text-amber" />
                ))}
                <meta itemProp="reviewRating" content={String(project.rating)} />
              </div>
              <p itemProp="reviewBody" className="text-lg md:text-xl leading-relaxed font-body italic mb-5">
                "{project.testimonial.text}"
              </p>
              <div className="flex items-center gap-3 pt-5 border-t-2 border-white/15">
                <div className="w-10 h-10 bg-lime text-navy font-display font-bold flex items-center justify-center border-2 border-cream">
                  {project.testimonial.author.charAt(0)}
                </div>
                <div>
                  <div itemProp="author" itemScope itemType="https://schema.org/Person">
                    <span itemProp="name" className="font-display font-bold text-sm uppercase tracking-wide text-white">
                      {project.testimonial.author}
                    </span>
                  </div>
                  <div className="text-[11px] text-lime font-display font-bold uppercase tracking-wider">
                    {project.testimonial.role}
                  </div>
                </div>
              </div>
            </div>
          </motion.blockquote>
        </article>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl p-0 bg-navy border-2 border-lime rounded-none [&>button]:hidden">
          <div className="relative">
            <img
              src={project.images[index]}
              alt={`${project.title} — photo ${index + 1}`}
              className="w-full max-h-[80vh] object-contain bg-navy"
            />
            <button
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-10 h-10 bg-navy border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={prev}
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-navy/80 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-navy/80 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

/* ─── Project Switcher Footer ───────────────────── */
const ProjectSwitcher = ({
  activeIndex,
  setActiveIndex,
}: {
  activeIndex: number;
  setActiveIndex: (i: number) => void;
}) => {
  const prevIdx = (activeIndex - 1 + projects.length) % projects.length;
  const nextIdx = (activeIndex + 1) % projects.length;

  return (
    <section className="bg-navy-deep py-8 border-y-[6px] border-lime">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-4 max-w-6xl mx-auto">
          <button
            onClick={() => setActiveIndex(prevIdx)}
            className="group flex items-center gap-4 bg-navy border-2 border-white/10 hover:border-lime transition-colors p-5 text-left"
          >
            <div className="w-10 h-10 bg-lime text-navy flex items-center justify-center flex-shrink-0 border-2 border-navy group-hover:-translate-x-1 transition-transform">
              <ChevronLeft className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-display font-bold uppercase tracking-[0.24em] text-lime mb-1">Previous Project</p>
              <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
                {projects[prevIdx].category}
              </p>
            </div>
          </button>

          <button
            onClick={() => setActiveIndex(nextIdx)}
            className="group flex items-center gap-4 bg-navy border-2 border-white/10 hover:border-lime transition-colors p-5 text-left justify-end"
          >
            <div className="text-right">
              <p className="text-[10px] font-display font-bold uppercase tracking-[0.24em] text-lime mb-1">Next Project</p>
              <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
                {projects[nextIdx].category}
              </p>
            </div>
            <div className="w-10 h-10 bg-lime text-navy flex items-center justify-center flex-shrink-0 border-2 border-navy group-hover:translate-x-1 transition-transform">
              <ChevronRight className="w-5 h-5" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── FAQ ─────────────────────────────────────────── */
const projectsFaqs = [
  {
    question: 'How big were the projects DeckPros NJ built?',
    answer: 'The three featured DeckPros NJ projects range from 1,800 to over 2,200 square feet. Project 01 (Morris County) is 1,800 sq ft pressure-treated wood, Project 02 (Sussex County) is 2,200 sq ft multi-level composite with under-deck living, and Project 03 (Somerset County) is a 2,000+ sq ft premium architectural composite deck.',
  },
  {
    question: 'How long did each deck project take to complete?',
    answer: 'Project completion times ranged from 5 to 7 weeks: 5 weeks for the pressure-treated wood deck, 7 weeks for the multi-level composite with under-deck living, and 6 weeks for the premium architectural composite. Timelines include design, permits, tear-out, and full install.',
  },
  {
    question: 'What materials did DeckPros NJ use for these projects?',
    answer: 'Materials varied by project: pressure-treated lumber with stainless cable rails and white vinyl lattice (Project 01); premium white composite with aluminum railings and a waterproof under-deck membrane (Project 02); and premium gray composite (Trex/TimberTech) with aluminum sub-frame and architectural detailing (Project 03).',
  },
  {
    question: 'Can I see the real homeowner reviews?',
    answer: 'Yes — every featured project includes a verified 5-star homeowner testimonial from the actual client in Morris, Sussex, or Somerset County, NJ. All reviews are collected after project completion.',
  },
  {
    question: 'Can DeckPros NJ build a similar deck for my home?',
    answer: 'Absolutely. Every deck featured in our portfolio can be replicated or customized for your home, yard, and budget. Request a free on-site consultation at (201) 657-0855 and we\'ll design a deck tailored to your NJ property.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream-dark">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Project Questions</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          About These <span className="text-lime">Builds</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {projectsFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Project ItemList + CreativeWork Schema ────── */
const ProjectsSchema = () => {
  const base = 'https://deckprosnj.com';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DeckPros NJ Featured Projects',
    numberOfItems: projects.length,
    itemListElement: projects.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'CreativeWork',
        name: p.title,
        description: p.description,
        contentLocation: p.county,
        dateCreated: p.date,
        url: `${base}/projects#${p.slug}`,
        creator: { '@type': 'Organization', name: 'DeckPros NJ' },
        review: {
          '@type': 'Review',
          reviewRating: { '@type': 'Rating', ratingValue: p.rating, bestRating: 5 },
          author: { '@type': 'Person', name: p.testimonial.author },
          reviewBody: p.testimonial.text,
        },
      },
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ──────────────────────────────────────── */
export default function Projects() {
  const [activeIndex, setActiveIndex] = useState(0);
  const project = projects[activeIndex];

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Project Portfolio · NJ Deck Builds"
        description="Explore 3 featured DeckPros NJ projects — pressure-treated wood in Morris County, multi-level composite in Sussex County, and premium architectural composite in Somerset County. Full photos, materials, timelines & reviews."
        canonical="/projects"
        keywords="deck projects NJ, DeckPros NJ portfolio, composite deck examples, wood deck build, multi-level deck NJ, deck case studies New Jersey"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Projects', url: '/projects' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={projectsFaqs} />
      <ProjectsSchema />

      <Header />
      <main>
        <PageHero />
        <ProjectTabs activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
        <ProjectDetails project={project} />
        <ProjectSwitcher activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
}
